import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom'
import { scaleLinear } from 'd3-scale'
import { line, area } from 'd3-shape'
import rk4 from 'ode-rk4'
import { range } from 'd3-array'
import { format } from 'd3-format'

function copy(x) {
  return Object.assign({},x)
}

const thousands = format("~s")

const handleRadius = 11
const handleOffset = 11 / Math.sqrt(2)
const handlePath = `M 0 0 L -${handleOffset} ${handleOffset} A 11 11 1 1 0 ${handleOffset} ${handleOffset} z`

const simulate = (f,t0,y0,step,tmax) => {
  let integrator = rk4(y0, f, t0, step)
  let t = t0
  const y = y0
  const ta = []
  const ya = []
  ta.push(t0)
  ya.push(copy(y))

  let peakInfected = 0
  let peakIndex = 0

  while(true){
    t = t+step
    if(t>tmax) break
    integrator=integrator.step()
    ya.push(copy(integrator.y))
    ta.push(t)

    if (integrator.y[1] > peakInfected) { 
      peakInfected = integrator.y[1];
      peakIndex = ta.length;
    }
  }

  return {
    t:ta,
    y:ya,
    peakInfected,
    peakIndex
  };
}

const SIRmodelWithWaves = () => {
  const svgRef = React.useRef()

  const [b1, setB1] = React.useState(0.12)
  const [b2, setB2] = React.useState(0.06)
  const [gamma, setGamma] = React.useState(0.05)

  const [periods, setPeriods] = useState([[70, 140], [280, 350]])
  
  const [simulationState, setSimulationState] = React.useState(null)
  const [simPeak, setSimPeak] = React.useState(null)
  const [pressed, setPressed] = useState(null)

  const I0 = 0.0001
  const step = 1
  const tmax = 7 * 78

  const clamp = (t) => {
    return Math.min(tmax, Math.max(0, t))
  }

  useEffect(() => {
    const withinPeriod = function(t) {
      return periods.reduce((memo, p) => {
        return memo || (t >= p[0] * step && t < p[1] * step)
      }, false)
    }

    const sir = (dydt, y, t) => {
      const isSD = withinPeriod(t)
      const beta = (isSD) ? b2 : b1

      dydt[0] = -beta * y[0] * y[1];
      dydt[1] = beta * y[0] * y[1] - gamma * y[1];
      dydt[2] = gamma *y[1];
    }

    const sir_sol = simulate(sir,0,[1.0-I0,I0,0.0],step,tmax)

    setSimPeak({
      index: sir_sol.peakIndex,
      value: sir_sol.peakInfected
    })

    setSimulationState(sir_sol.y)
  }, [periods, b1, b2])

  const populationSum = 100000
  const totalCasesAtTmax = (simulationState === null) ? 0 : simulationState[simulationState.length - 1][1] + simulationState[simulationState.length - 1][2]

  const heightScale = scaleLinear()
  .domain([0, 1])
  .range([400, 100])

  const width = Math.min(window.innerWidth, 600)

  const widthScale = scaleLinear()
    .domain([0, tmax / step])
    .range([25, width - 100])

  const iArea = area()
    .x((d, i) => widthScale(i))
    .y1(d => heightScale(d[1]))
    .y0(heightScale(0));

  const rArea = area()
    .x((d, i) => widthScale(i))
    .y1(d => heightScale(d[1] + d[2]))
    .y0(d => heightScale(d[1]));

  const sArea = area()
    .x((d, i) => widthScale(i))
    .y1(heightScale(1))
    .y0(d => heightScale(d[1] + d[2]));

  const triggerInteraction = (e) => {
    e.preventDefault()

    const mx = (e.touches) ? e.touches[0].clientX : e.clientX
    const rect = svgRef.current.getBoundingClientRect()
    const t = widthScale.invert(mx - rect.left)

    let i = 0
    let period = null
    let anchor = null
    let previousValue = null

    periods.forEach((p, i) => {
      const distanceFromStart = Math.abs(p[0] - t)
      const distanceFromEnd = Math.abs(p[1] - t)

      const closerToStart = distanceFromStart < distanceFromEnd
      const closeEnough = ((closerToStart) ? distanceFromStart : distanceFromEnd) < 11
      
      if (closeEnough) {
        period = i
        anchor = closerToStart
        previousValue = periods[period].slice(0)
      } else {
        if (t >= p[0] && t < p[1]) {
          period = i
          previousValue = periods[period].slice(0)
        }
      }
    })

    if (period === null) {
      period = periods.length
      anchor = true
      previousValue = [t - 2, t + 5]

      const newPeriods = periods.slice(0)
      newPeriods.push(previousValue)
      
      setPeriods(newPeriods)
    }

    setPressed({ 
      mouseStart: t,
      period,
      anchor,
      previousValue
    })
  }

  const stopInteraction = () => {
    setPressed(null)
  }

  const drag = (e) => {
    e.preventDefault()

    if (pressed) {

      const mx = (e.touches) ? e.touches[0].clientX : e.clientX
      const rect = svgRef.current.getBoundingClientRect()
    
      const t = widthScale.invert(mx - rect.left)
      const delta = t - pressed.mouseStart
      
      if (pressed.period !== null) {
        if (pressed.anchor !== null) {
          const anchorId = (pressed.anchor) ? 0 : 1 
          const nextValue = clamp(pressed.previousValue[anchorId] + delta)

          const nextPair = [nextValue, pressed.previousValue[(pressed.anchor) ? 1 : 0]]

          periods[pressed.period] = nextPair.sort((a, b) => a - b)
        } else {
          periods[pressed.period][0] = clamp(pressed.previousValue[0] + delta)
          periods[pressed.period][1] = clamp(pressed.previousValue[1] + delta)
        }

        setPeriods(periods.slice(0))
      }
    }
  }

  const removePeriod = (i) => {
    const remains = periods.slice(0)
    remains.splice(i, 1)
    setPeriods(remains)
  }


  return ReactDOM.createPortal(
    <div>
      <span className="sir-with-wave-legend">
        <span style={{ marginRight: "10px", display: "inline-block" }}><span style={{ display: "inline-block", width: 12, height: 12, background: "#eee", borderRadius: "2px" }}></span>&nbsp;Susceptible&nbsp;Population</span>
        <span style={{ marginRight: "10px", display: "inline-block" }}><span style={{ display: "inline-block", width: 12, height: 12, background: "#c60", borderRadius: "2px" }}></span>&nbsp;Infected&nbsp;Population</span>
        <span style={{ marginRight: "10px", display: "inline-block" }}><span style={{ display: "inline-block", width: 12, height: 12, background: "#979", borderRadius: "2px" }}></span>&nbsp;Recovered&nbsp;Population</span>
      </span>

      <svg width="100%" height="560" ref={svgRef}>
        {(simulationState) ?
          <path d={sArea(simulationState)} stroke="none" fill="#eee" />
        : null}

        {periods.map((p, i) => {
          return <g key={`period-${i}`}>
            <rect
              x={widthScale(p[0])}
              width={widthScale(p[1]) - widthScale(p[0])}
              y={20}
              height={500}
              fill={"#aaa"}
              opacity={0.2}
            />
            <g transform={`translate(${widthScale(p[0])},520)`}>
              <path d={handlePath} fill={"#888"} />
            </g>
            <g transform={`translate(${widthScale(p[1])},520)`}>
              <path d={handlePath} fill={"#888"} />
            </g>
            <rect 
              x={widthScale(p[0])}
              y={515}
              width={widthScale(p[1]) - widthScale(p[0])}
              height={5}
              style={{ fill: "url(#diagonal-stripe-2)" }} 
            />
            <g transform={`translate(${widthScale(p[0]) + 3},${heightScale(0) + 60})`}>
              <text 
                style={{ fontSize: "10px", fill: "#222" }}
              >
                Start
              </text>
              <text 
                style={{ fontSize: "10px", fill: "#000", fontWeight: "bold" }}
                y="12"
              >
                Day {Math.round(p[0])}
              </text>
              <text 
                style={{ fontSize: "10px", fill: "#222" }}
                y="28"
              >
                Length
              </text>
              <text 
                style={{ fontSize: "10px", fill: "#000", fontWeight: "bold" }}
                y="40"
              >
                {Math.round(p[1] - p[0])} days
              </text>
            </g>
          </g>
        })}

        {(simulationState) ?
          <React.Fragment>
            <path d={rArea(simulationState)} stroke="none" opacity={0.7} fill="#979" />
            <path d={iArea(simulationState)} stroke="none" fill="#c60" />
          </React.Fragment>
        : null}

        <g className="sir-x-axis" transform="translate(0,400)">
          <line x1="0" x2="600" stroke="black" />
          {range(Math.floor(tmax / 7)).map((i) => {
            const x = widthScale(i * 7 / step)

            return <g key={i} transform={`translate(${x},0)`}>
              <line y2={(i % 3 === 0) ? 7 : 4} stroke="black" />
              {(i % 5 === 0) ?
                <text y="16" style={{ fontSize: "10px", fill: "#888" }} textAnchor="middle">{i}</text> 
              : null}
            </g>
          })}
          <text y="32" x={widthScale(0) - 3} style={{ fontSize: "10px", fill: "#444", fontWeight: "bold" }} textAnchor="start">Weeks since 100th case</text> 
        </g>
        
        <g className="sir-y-axis">
          {range(4).map((i) => {
            const j = i + 1
            const value = j / 4
            return <g key={i} transform={`translate(0,${heightScale(value)})`}>
              <line x2={width} stroke={(i === 3) ? "#000" : "#ccc"} opacity={0.5} />
              <text y="-2" x="1" textAnchor="start" style={{ fontSize: "10px", fill: "#222" }}>{thousands(value * populationSum)}</text>
            </g>
          })}
        </g>

      <g transform={`translate(${width - 90},${heightScale(totalCasesAtTmax)})`}>
        <polyline 
          points="-8,0 -3,3 -3,-3"
          fill="black"
        />  
        <text style={{ fontSize: "10px", fill: "#222" }} y="17">Total Cases</text>
        <text style={{ fontSize: "10px", fill: "#222" }} y="28">After 1.5 years</text>
        <text style={{ fontSize: "10px", fill: "#000", fontWeight: "bold" }} y="3" >~{Math.round(totalCasesAtTmax * populationSum / 100) * 100}</text>
      </g>

      {(simPeak) ?
      <g transform={`translate(${widthScale(simPeak.index)},0)`}>
        <line 
          y1="50" 
          y2={heightScale(simPeak.value) - 3} 
          stroke="#a00"
        />
        <polyline 
          points="0,50 5,53 0,56"
          fill="#a00"
        />
        <text x="4" y="70" style={{ fontSize: "10px", fill: "#a00" }}>Infections at Peak</text>
      <text x="4" y="80" style={{ fontSize: "10px", fill: "#a00", fontWeight: "bold" }}>~{Math.round(simPeak.value * populationSum / 100) * 100}</text>
      </g>
      : null }

      <text y={heightScale(1) - 16} x={width - 90} textAnchor="start" style={{ fontSize: "10px", fill: "#000" }}>Total Population</text>
      <text y={heightScale(1) - 5} x={width - 90} textAnchor="start" style={{ fontSize: "10px", fill: "#000", fontWeight: "bold" }}>{populationSum}</text>

      <rect
        fill="red"
        opacity={0}
        x={widthScale(0) - 11}
        width={widthScale(tmax) - widthScale(0) + 22}
        height={560}
        onTouchStart={triggerInteraction}
        onMouseDown={triggerInteraction}
        onTouchEnd={stopInteraction}
        onMouseUp={stopInteraction}
        onMouseMove={drag}
        onTouchMove={drag}
      />

      {periods.map((p, i) => {
        return <g key={i} transform={`translate(${widthScale(p[1])},20)`}>
          <circle 
            r="11"
            fill={"#aaa"}
          />
          <polyline 
            points="2,0 6,4 4,6 0,2 -4,6 -6,4 -2,0 -6,-4 -4,-6 0,-2 4,-6 6,-4"
            fill="#fff"
          />
          <circle 
            r="11"
            fill={"#aaa"}
            opacity={0}
            onClick={() => {
              removePeriod(i)
            }}
          />
        </g>
      })}
        <g transform={`translate(${width},435)`}>
          <text style={{ fontSize: "10px", fill: "#444" }} textAnchor="end">
            Toy simulation
          </text>
          <text y="13" style={{ fontSize: "10px", fill: "#444" }} textAnchor="end">
            β<tspan baselineShift="sub" fontSize="7px">1</tspan> = {b1}
          </text>
          <text y="26" style={{ fontSize: "10px", fill: "#444" }} textAnchor="end">
            β<tspan baselineShift="sub" fontSize="7px">2</tspan> = {b2}
          </text>
          <text y="39" style={{ fontSize: "10px", fill: "#444" }} textAnchor="end">
            γ = {gamma}
          </text>
        </g>
      </svg>
      
    </div>
  , document.getElementById("sir-model-with-waves"));
};

export default SIRmodelWithWaves;